import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { toast } from "sonner";
import { Package, Tags, Plus, Pencil, Trash2, LogOut, X, ExternalLink, Search } from "lucide-react";

const emptyProduct = { title: "", description: "", price: "", image: "", amazon_url: "", category_slug: "", rating: 4.5, featured: false, meta_title: "", meta_description: "" };
const emptyCategory = { name: "", slug: "", description: "", image: "", meta_title: "", meta_description: "" };

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("products");
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [productModal, setProductModal] = useState(null); // {data, isNew}
  const [categoryModal, setCategoryModal] = useState(null);
  const [seo, setSeo] = useState(null);
  const [savingSeo, setSavingSeo] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/admin/login");
  }, [loading, user, navigate]);

  const loadAll = () => {
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/categories").then((r) => setCategories(r.data));
    api.get("/seo").then((r) => setSeo(r.data));
  };
  useEffect(() => { loadAll(); }, []);

  const saveSeo = async () => {
    setSavingSeo(true);
    try {
      const { data } = await api.put("/seo", seo);
      setSeo(data);
      toast.success("SEO settings saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSavingSeo(false);
    }
  };

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center text-zinc-400">Loading…</div>;

  const saveProduct = async () => {
    const d = productModal.data;
    if (!d.title || !d.amazon_url || !d.category_slug) {
      toast.error("Title, Amazon URL and category are required");
      return;
    }
    try {
      const payload = { ...d, rating: parseFloat(d.rating) || 4.5 };
      if (productModal.isNew) await api.post("/products", payload);
      else await api.put(`/products/${d.id}`, payload);
      toast.success(productModal.isNew ? "Product added" : "Product updated");
      setProductModal(null);
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Product deleted");
    loadAll();
  };

  const saveCategory = async () => {
    const d = categoryModal.data;
    if (!d.name || !d.slug) { toast.error("Name and slug are required"); return; }
    try {
      if (categoryModal.isNew) await api.post("/categories", d);
      else await api.put(`/categories/${d.id}`, d);
      toast.success(categoryModal.isNew ? "Category added" : "Category updated");
      setCategoryModal(null);
      loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const deleteCategory = async (id) => {
    if (!window.confirm("Delete this category?")) return;
    await api.delete(`/categories/${id}`);
    toast.success("Category deleted");
    loadAll();
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="w-60 bg-[#09090B] text-white flex flex-col fixed h-full">
        <div className="p-6 font-display font-black text-xl tracking-tighter uppercase border-b border-zinc-800">
          Curated<span className="text-[#FF2A00]">.</span> <span className="text-xs font-normal text-zinc-500 tracking-normal normal-case">admin</span>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          <button data-testid="tab-products" onClick={() => setTab("products")} className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${tab === "products" ? "bg-[#FF2A00] text-white" : "text-zinc-400 hover:text-white hover:bg-zinc-800"}`}>
            <Package size={16} /> Products
          </button>
          <button data-testid="tab-categories" onClick={() => setTab("categories")} className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${tab === "categories" ? "bg-[#FF2A00] text-white" : "text-zinc-400 hover:text-white hover:bg-zinc-800"}`}>
            <Tags size={16} /> Categories
          </button>
          <button data-testid="tab-seo" onClick={() => setTab("seo")} className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${tab === "seo" ? "bg-[#FF2A00] text-white" : "text-zinc-400 hover:text-white hover:bg-zinc-800"}`}>
            <Search size={16} /> SEO
          </button>
        </nav>
        <div className="p-4 border-t border-zinc-800">
          <div className="text-xs text-zinc-500 mb-2 px-2 truncate">{user.email}</div>
          <button data-testid="logout-btn" onClick={() => { logout(); navigate("/"); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 ml-60 p-10">
        {tab === "products" ? (
          <div>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-display text-3xl font-bold tracking-tight">Products</h1>
                <p className="text-sm text-zinc-500">{products.length} total</p>
              </div>
              <button data-testid="add-product-btn" onClick={() => setProductModal({ data: { ...emptyProduct }, isNew: true })} className="inline-flex items-center gap-2 bg-black hover:bg-[#FF2A00] text-white px-5 py-2.5 text-sm font-medium transition-colors">
                <Plus size={16} /> Add product
              </button>
            </div>
            <div className="border border-zinc-200 bg-white">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-zinc-200 text-left text-xs uppercase tracking-wider text-zinc-500">
                    <th className="p-4 font-bold">Product</th>
                    <th className="p-4 font-bold">Category</th>
                    <th className="p-4 font-bold">Price</th>
                    <th className="p-4 font-bold">Featured</th>
                    <th className="p-4 font-bold text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((p) => (
                    <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`product-row-${p.id}`}>
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <img src={p.image || "https://placehold.co/100x100/f4f4f5/09090b?text=N/A"} alt="" className="w-10 h-10 object-cover border border-zinc-200" />
                          <span className="font-medium line-clamp-1 max-w-xs">{p.title}</span>
                        </div>
                      </td>
                      <td className="p-4 capitalize text-zinc-600">{p.category_slug}</td>
                      <td className="p-4">{p.price}</td>
                      <td className="p-4">{p.featured ? <span className="text-[#FF2A00] font-medium">Yes</span> : <span className="text-zinc-400">No</span>}</td>
                      <td className="p-4">
                        <div className="flex items-center justify-end gap-2">
                          <button data-testid={`edit-product-${p.id}`} onClick={() => setProductModal({ data: { ...p }, isNew: false })} className="p-2 hover:bg-zinc-100 text-zinc-600 hover:text-black transition-colors"><Pencil size={15} /></button>
                          <button data-testid={`delete-product-${p.id}`} onClick={() => deleteProduct(p.id)} className="p-2 hover:bg-red-50 text-zinc-600 hover:text-red-600 transition-colors"><Trash2 size={15} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {products.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-zinc-400">No products yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        ) : tab === "categories" ? (
          <div>
            <div className="flex items-center justify-between mb-8">
              <div>
                <h1 className="font-display text-3xl font-bold tracking-tight">Categories</h1>
                <p className="text-sm text-zinc-500">{categories.length} total</p>
              </div>
              <button data-testid="add-category-btn" onClick={() => setCategoryModal({ data: { ...emptyCategory }, isNew: true })} className="inline-flex items-center gap-2 bg-black hover:bg-[#FF2A00] text-white px-5 py-2.5 text-sm font-medium transition-colors">
                <Plus size={16} /> Add category
              </button>
            </div>
            <div className="grid md:grid-cols-3 gap-4">
              {categories.map((c) => (
                <div key={c.id} className="border border-zinc-200 bg-white" data-testid={`category-card-${c.slug}`}>
                  <img src={c.image || "https://placehold.co/600x300/f4f4f5/09090b?text=No+Image"} alt={c.name} className="w-full h-32 object-cover" />
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-display font-bold text-lg">{c.name}</h3>
                      <span className="text-xs text-zinc-400">/{c.slug}</span>
                    </div>
                    <p className="text-sm text-zinc-500 mt-1 line-clamp-2">{c.description}</p>
                    <div className="flex gap-2 mt-4">
                      <button data-testid={`edit-category-${c.slug}`} onClick={() => setCategoryModal({ data: { ...c }, isNew: false })} className="flex-1 inline-flex items-center justify-center gap-1.5 border border-zinc-300 hover:border-black py-2 text-sm transition-colors"><Pencil size={14} /> Edit</button>
                      <button data-testid={`delete-category-${c.slug}`} onClick={() => deleteCategory(c.id)} className="px-3 border border-zinc-300 hover:border-red-500 hover:text-red-600 transition-colors"><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="max-w-2xl">
            <div className="mb-8">
              <h1 className="font-display text-3xl font-bold tracking-tight">SEO settings</h1>
              <p className="text-sm text-zinc-500">Global search-engine optimization for your storefront.</p>
            </div>
            {seo ? (
              <div className="border border-zinc-200 bg-white p-6 space-y-4" data-testid="seo-form">
                <div className="grid md:grid-cols-2 gap-4">
                  <Field label="Site name" testid="seo-site-name" value={seo.site_name} onChange={(v) => setSeo({ ...seo, site_name: v })} />
                  <Field label="Site URL" placeholder="https://yourdomain.com" testid="seo-site-url" value={seo.site_url} onChange={(v) => setSeo({ ...seo, site_url: v })} />
                </div>
                <Field label="Default page title" testid="seo-default-title" value={seo.default_title} onChange={(v) => setSeo({ ...seo, default_title: v })} />
                <Field label="Default meta description" textarea testid="seo-default-description" value={seo.default_description} onChange={(v) => setSeo({ ...seo, default_description: v })} />
                <Field label="Default keywords (comma separated)" testid="seo-keywords" value={seo.default_keywords} onChange={(v) => setSeo({ ...seo, default_keywords: v })} />
                <Field label="Social share image (OG image URL)" testid="seo-og-image" value={seo.og_image} onChange={(v) => setSeo({ ...seo, og_image: v })} />
                <Field label="Twitter handle" placeholder="@yourbrand" testid="seo-twitter" value={seo.twitter_handle} onChange={(v) => setSeo({ ...seo, twitter_handle: v })} />
                <div className="flex items-center gap-3 pt-2">
                  <button data-testid="save-seo-btn" onClick={saveSeo} disabled={savingSeo} className="bg-black hover:bg-[#FF2A00] text-white px-6 py-2.5 font-medium transition-colors disabled:opacity-60">{savingSeo ? "Saving…" : "Save SEO settings"}</button>
                  <a href={`${(seo.site_url || "").replace(/\/$/, "")}/api/sitemap.xml`} target="_blank" rel="noopener noreferrer" data-testid="view-sitemap-link" className="inline-flex items-center gap-1.5 text-sm text-zinc-600 hover:text-black transition-colors">View sitemap.xml <ExternalLink size={14} /></a>
                </div>
                <p className="text-xs text-zinc-400 pt-1">Sitemap & robots.txt are generated automatically from your products and categories. Per-product and per-category meta can be set from their edit forms.</p>
              </div>
            ) : (
              <p className="text-zinc-400">Loading…</p>
            )}
          </div>
        )}
      </main>

      {/* Product Modal */}
      {productModal && (
        <Modal title={productModal.isNew ? "Add product" : "Edit product"} onClose={() => setProductModal(null)} testid="product-modal">
          <div className="space-y-4">
            <Field label="Title *" testid="pm-title" value={productModal.data.title} onChange={(v) => setProductModal({ ...productModal, data: { ...productModal.data, title: v } })} />
            <Field label="Description" textarea testid="pm-description" value={productModal.data.description} onChange={(v) => setProductModal({ ...productModal, data: { ...productModal.data, description: v } })} />
            <div className="grid grid-cols-2 gap-4">
              <Field label="Price" placeholder="$29.99" testid="pm-price" value={productModal.data.price} onChange={(v) => setProductModal({ ...productModal, data: { ...productModal.data, price: v } })} />
              <Field label="Rating" type="number" testid="pm-rating" value={productModal.data.rating} onChange={(v) => setProductModal({ ...productModal, data: { ...productModal.data, rating: v } })} />
            </div>
            <Field label="Image URL" testid="pm-image" value={productModal.data.image} onChange={(v) => setProductModal({ ...productModal, data: { ...productModal.data, image: v } })} />
            <Field label="Amazon Affiliate URL *" testid="pm-amazon" value={productModal.data.amazon_url} onChange={(v) => setProductModal({ ...productModal, data: { ...productModal.data, amazon_url: v } })} />
            <div>
              <label className="text-xs uppercase tracking-wider font-bold text-zinc-500">Category *</label>
              <select data-testid="pm-category" value={productModal.data.category_slug} onChange={(e) => setProductModal({ ...productModal, data: { ...productModal.data, category_slug: e.target.value } })} className="w-full border border-zinc-300 focus:border-black outline-none px-3 py-2.5 mt-1.5 bg-white">
                <option value="">Select a category</option>
                {categories.map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input data-testid="pm-featured" type="checkbox" checked={productModal.data.featured} onChange={(e) => setProductModal({ ...productModal, data: { ...productModal.data, featured: e.target.checked } })} className="w-4 h-4 accent-[#FF2A00]" />
              Show on homepage (featured)
            </label>
            <div className="border-t border-zinc-200 pt-4 mt-2">
              <div className="text-xs uppercase tracking-wider font-bold text-zinc-400 mb-3">SEO (optional)</div>
              <div className="space-y-4">
                <Field label="Meta title" placeholder="Overrides page title" testid="pm-meta-title" value={productModal.data.meta_title} onChange={(v) => setProductModal({ ...productModal, data: { ...productModal.data, meta_title: v } })} />
                <Field label="Meta description" textarea placeholder="Overrides page description" testid="pm-meta-description" value={productModal.data.meta_description} onChange={(v) => setProductModal({ ...productModal, data: { ...productModal.data, meta_description: v } })} />
              </div>
            </div>
          </div>
          <button data-testid="save-product-btn" onClick={saveProduct} className="w-full bg-black hover:bg-[#FF2A00] text-white py-3 font-medium mt-6 transition-colors">Save product</button>
        </Modal>
      )}

      {/* Category Modal */}
      {categoryModal && (
        <Modal title={categoryModal.isNew ? "Add category" : "Edit category"} onClose={() => setCategoryModal(null)} testid="category-modal">
          <div className="space-y-4">
            <Field label="Name *" testid="cm-name" value={categoryModal.data.name} onChange={(v) => setCategoryModal({ ...categoryModal, data: { ...categoryModal.data, name: v, slug: categoryModal.isNew ? v.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") : categoryModal.data.slug } })} />
            <Field label="Slug *" testid="cm-slug" value={categoryModal.data.slug} onChange={(v) => setCategoryModal({ ...categoryModal, data: { ...categoryModal.data, slug: v } })} />
            <Field label="Description" textarea testid="cm-description" value={categoryModal.data.description} onChange={(v) => setCategoryModal({ ...categoryModal, data: { ...categoryModal.data, description: v } })} />
            <Field label="Image URL" testid="cm-image" value={categoryModal.data.image} onChange={(v) => setCategoryModal({ ...categoryModal, data: { ...categoryModal.data, image: v } })} />
            <div className="border-t border-zinc-200 pt-4 mt-2 space-y-4">
              <div className="text-xs uppercase tracking-wider font-bold text-zinc-400">SEO (optional)</div>
              <Field label="Meta title" placeholder="Overrides page title" testid="cm-meta-title" value={categoryModal.data.meta_title} onChange={(v) => setCategoryModal({ ...categoryModal, data: { ...categoryModal.data, meta_title: v } })} />
              <Field label="Meta description" textarea placeholder="Overrides page description" testid="cm-meta-description" value={categoryModal.data.meta_description} onChange={(v) => setCategoryModal({ ...categoryModal, data: { ...categoryModal.data, meta_description: v } })} />
            </div>
          </div>
          <button data-testid="save-category-btn" onClick={saveCategory} className="w-full bg-black hover:bg-[#FF2A00] text-white py-3 font-medium mt-6 transition-colors">Save category</button>
        </Modal>
      )}
    </div>
  );
}

const Modal = ({ title, onClose, children, testid }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
    <div data-testid={testid} className="bg-white border border-black w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
      <div className="flex items-center justify-between p-5 border-b border-zinc-200 sticky top-0 bg-white">
        <h2 className="font-display text-xl font-bold">{title}</h2>
        <button onClick={onClose} className="p-1 hover:text-[#FF2A00] transition-colors"><X size={20} /></button>
      </div>
      <div className="p-6">{children}</div>
    </div>
  </div>
);

const Field = ({ label, value, onChange, textarea, type = "text", placeholder, testid }) => (
  <div>
    <label className="text-xs uppercase tracking-wider font-bold text-zinc-500">{label}</label>
    {textarea ? (
      <textarea data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} rows={3} className="w-full border border-zinc-300 focus:border-black outline-none px-3 py-2.5 mt-1.5 transition-colors resize-none" />
    ) : (
      <input data-testid={testid} type={type} step={type === "number" ? "0.1" : undefined} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} className="w-full border border-zinc-300 focus:border-black outline-none px-3 py-2.5 mt-1.5 transition-colors" />
    )}
  </div>
);
